import { CameraType } from '@thive/canvas';
import { FloorplanRevision } from '@thive/fddm-types';

import { assertNever } from '@/helpers/utils';
import { isCanvasModule } from '@/modules/common/helpers/navigation';
import { logger } from '@common/logging/logger';
import { DEFAULT_ENABLED_LOAD_TYPE_IDS } from '@/modules/common/constants/loadTypes';
import { ModalId } from '@/modules/common/components/ModalManager/constants';
import { SizeShort } from '@/modules/common/types/general';
import { DistconServiceFactory, IDistconService } from '@/modules/connections';
import { ToolId } from '@modules/toolbar/types';
import { useAppMetaStore } from '@/modules/floorplanV2/store/project/projectState';
import { replaceURLPath } from '@/modules/app/helpers';
import KeyboardUtil from '@modules/keyboard/KeyboardUtil';
import { FloorplanDesignSelector } from '@/modules/floorplanDesign/store/selectors';
import { useFloorplanDesignStore } from '@/modules/floorplanDesign/store/useFloorplanDesignStore';
import { initializeNames } from '@/modules/floorplanDesign/helpers/name';
import {
  areasToFlatCollection,
  hasNoAreas,
  hasNoTemplates,
} from '@/modules/floorplanDesign/helpers';
import { templatesToFlatCollection } from '@/modules/floorplanDesign/helpers/helpers';
import { IFlowService } from '@/modules/flowsV2/service/IFlowService';
import { FlowServiceFactory } from '@/modules/flowsV2/service/FlowServiceFactory';
import { CanvasServiceFactory } from '@/modules/canvas/services/Canvas/CanvasServiceFactory';
import { ICanvasService } from '@modules/canvas/services/Canvas/ICanvasService';
import { AreaPickerServiceFactory } from '@/modules/canvas/services/AreaPicker/AreaPickerServiceFactory';
import { IAreaPickerService } from '@/modules/canvas/services/AreaPicker/IAreaPickerService';
import { FddmServiceFactory } from '@/modules/canvas/services/Floorplan/FddmServiceFactory';
import { IFddmService } from '@/modules/canvas/services/Floorplan/IFddmService';
import {
  ProjectServiceConnector,
  IProjectServiceConnector,
} from '@/modules/api/ProjectApiConnector';
import { useVehicleStore } from '@/modules/vehicles/store/useVehicleStore';
import { useLoadCarrierStore } from '@/modules/loadCarriers/store/useLoadCarrierStore';
import { IReferenceImageService } from '@/modules/referenceImage/service/IReferenceImageService';
import { ReferenceImageServiceFactory } from '@/modules/referenceImage/service/ReferenceImageServiceFactory';
import { DEFAULT_ENABLED_VEHICLE_TYPE_IDS } from '@/modules/vehicles';
import { IVehicleService } from '@/modules/vehicles/service/IVehicleService';
import { VehicleServiceFactory } from '@/modules/vehicles/service/VehicleServiceFactory';
import { IFpsService } from '@/modules/floorplanV2/service/IFpsService';
import { FpsServiceFactory } from '@/modules/floorplanV2/service/FpsServiceFactory';

import { useAppStore, CanvasOverlayRequest } from '../store/useAppStore';
import { FloorplanInteractionMode, LoadingType, Module } from '../types';
import { IAppService } from './IAppService';

export class AppServiceImpl implements IAppService {
  private appStore: typeof useAppStore;
  private vehicleStore: typeof useVehicleStore;
  private loadCarrierStore: typeof useLoadCarrierStore;
  private appMetaStore: typeof useAppMetaStore;
  private floorplanDesignStore: typeof useFloorplanDesignStore;
  private fddmSvc: IFddmService;
  private canvasSvc: ICanvasService;
  private flowSvc: IFlowService;
  private distconSvc: IDistconService;
  private projectSvc: IProjectServiceConnector;
  private referenceImageSvc: IReferenceImageService;
  private vehicleSvc: IVehicleService;
  private fpsSvc: IFpsService;

  public constructor(
    appStore: typeof useAppStore,
    appMetaStore: typeof useAppMetaStore,
    vehicleStore: typeof useVehicleStore,
    loadCarrierStore: typeof useLoadCarrierStore,
    floorplanDesignStore: typeof useFloorplanDesignStore,
  ) {
    this.appStore = appStore;
    this.vehicleStore = vehicleStore;
    this.loadCarrierStore = loadCarrierStore;
    this.appMetaStore = appMetaStore;
    this.floorplanDesignStore = floorplanDesignStore;
    this.fddmSvc = FddmServiceFactory.getInstance().createFddmService();
    this.canvasSvc = CanvasServiceFactory.getInstance().createCanvasService();
    this.flowSvc = FlowServiceFactory.getInstance().createFlowService();
    this.distconSvc = DistconServiceFactory.getInstance().createDistconService();
    this.projectSvc = ProjectServiceConnector.getInstance();
    this.referenceImageSvc = ReferenceImageServiceFactory.getInstance().createService();
    this.vehicleSvc = VehicleServiceFactory.getInstance().createVehicleService();
    this.fpsSvc = FpsServiceFactory.getInstance().createFpsService();
  }

  //
  // Toolbar
  //

  public activateTool(tool: ToolId): void {
    const { mode, selectedToolId } = this.appStore.getState();

    const selectOnlyToolIsAllowed =
      tool === 'move_reference' ||
      tool === 'scale_reference' ||
      tool === 'translate_by_move_origin';

    const toolIsAllowed =
      (mode === 'SELECT_ONLY' && selectOnlyToolIsAllowed) ||
      mode === 'EDITABLE' ||
      tool === 'cursor' ||
      tool === 'pan';

    const nextTool = toolIsAllowed ? tool : 'cursor';
    const previousTool = selectedToolId;

    if (previousTool !== nextTool) {
      this.deactivateTool(previousTool);
    }

    this.appStore.getState().toolbar.setSelectedToolId(nextTool);
    this.canvasSvc.setActiveTool(nextTool);

    switch (nextTool) {
      case 'transport_flow':
      case 'consolidation_flow':
      case 'external_flow':
        this.deselectAll();
        this.flowSvc.activateTool(nextTool);
        break;

      case 'distcon':
        this.distconSvc.activateTool();
        break;

      default:
        break;
    }
  }

  public resetTool(): void {
    const tool = this.appStore.getState().selectedToolId;
    this.deactivateTool(tool);
    this.appStore.getState().toolbar.setSelectedToolId('cursor');
    this.setActivePopoverId(null);
    this.canvasSvc.setActiveTool('cursor');
  }

  //
  // Module
  //

  public getActiveModule(): Module {
    return this.appStore.getState().activeModule;
  }

  public goToModule(module: Module): void {
    if (this.getActiveModule() !== module) {
      this.moduleLifeCycleEnd();
      this.moduleLifeCycleStart(module);
    }
  }

  private moduleLifeCycleStart(module: Module): void {
    if (!this.canvasSvc.canvasInstanceIsInitialized()) return;

    if (!isCanvasModule(module)) return;

    this.appStore.getState().module.setModule(module);
    KeyboardUtil.getInstance().activateModuleKeyboardEvents(module);
    this.syncFloorplanInteractionMode();

    switch (module) {
      case 'canvas_setup':
        this.canvasSvc.setCameraType(CameraType.TopView);
        this.deselectAll();
        this.activateTool('pan');
        return;

      case 'canvas_layout':
        this.canvasSvc.setCameraType(CameraType.TopView);
        this.deselectAll();
        this.setCanvasOverlay(null);
        this.setClickableElementIds([]);
        this.setDisabledElementIds([]);
        this.setActivePopoverId(null);
        this.canvasSvc.unlockSelection();
        this.activateTool('cursor');
        return;

      case 'canvas_viewer':
        this.canvasSvc.setCameraType(CameraType.Perspective);
        this.deselectAll();
        this.canvasSvc.lockSelection();
        this.activateTool('pan');
        this.canvasSvc.prepare3DView();
        return;

      case 'visualization':
        this.canvasSvc.setCameraType(CameraType.Perspective);
        this.deselectAll();
        this.canvasSvc.lockSelection();
        this.activateTool('pan');
        return;

      case 'canvas_simulation':
        this.canvasSvc.setCameraType(CameraType.TopView);
        this.deselectAll();
        this.canvasSvc.lockSelection();
        this.activateTool('pan');
        return;

      default:
        assertNever(module);
    }
  }

  private moduleLifeCycleEnd(): void {
    if (!this.canvasSvc.canvasInstanceIsInitialized()) return;
    this.setActivePopoverId(null);
    const module = this.appStore.getState().activeModule;

    if (!isCanvasModule(module)) return;

    switch (module) {
      case 'canvas_setup':
        return;

      case 'canvas_layout':
        this.deselectAll();
        return;

      case 'canvas_viewer':
        this.canvasSvc.prepare2DView();
        return;

      case 'visualization':
        return;

      case 'canvas_simulation':
        return;

      default:
        assertNever(module);
    }
  }

  //
  // Floorplan mode
  //

  public setFloorplanInteractionMode(mode: FloorplanInteractionMode): void {
    this.appStore.getState().floorplanInteractionMode.setMode(mode);
    KeyboardUtil.getInstance().setMode(mode);

    switch (mode) {
      case 'EDITABLE':
        this.applyEditableModeState();
        break;

      case 'SELECT_ONLY':
        this.applySelectOnlyModeState();
        break;

      case 'READONLY':
        this.applyReadonlyModeState();
        break;

      default:
        assertNever(mode);
    }
  }

  private applyReadonlyModeState(): void {
    this.resetSelection();
    this.setActivePopoverId(null);
    this.setCanvasOverlay(null);
    this.setClickableElementIds([]);
    this.setDisabledElementIds([]);
    this.appStore.getState().toolbar.setSelectedToolId('cursor');

    if (!this.canvasSvc.canvasInstanceIsInitialized()) {
      return;
    }

    this.canvasSvc.setInteractivityMode('READONLY');
    this.canvasSvc.deselectAll();
    this.activateTool('cursor');
  }

  private applySelectOnlyModeState(): void {
    this.setActivePopoverId(null);
    this.setCanvasOverlay(null);
    this.setClickableElementIds([]);
    this.setDisabledElementIds([]);
    this.appStore.getState().toolbar.setSelectedToolId('cursor');

    if (!this.canvasSvc.canvasInstanceIsInitialized()) {
      return;
    }

    this.canvasSvc.setInteractivityMode('SELECT_ONLY');
    this.canvasSvc.unlockSelection();
    this.activateTool('cursor');
  }

  private applyEditableModeState(): void {
    if (!this.canvasSvc.canvasInstanceIsInitialized()) {
      return;
    }

    this.canvasSvc.setInteractivityMode('EDITABLE');
    this.canvasSvc.unlockSelection();
  }

  //
  // Selection
  //

  public selectAll(): void {
    const {
      design: { areas, obstacles, templates, logicalSpaces },
      uxonly: { groups },
    } = this.fddmSvc.getModel();
    const areaCollection = areasToFlatCollection(areas);

    const ids = [
      areaCollection.map((area) => area.id),
      obstacles.map((obstacle) => obstacle.id),
      templatesToFlatCollection(templates).map((template) => template.group.id),
      logicalSpaces.map((logicalSpace) => logicalSpace.id),
      groups.map((group) => group.id),
    ].flat();

    this.setSelectedIds(ids);
    this.canvasSvc.setSelection(ids);
  }

  public setSelectedIds(ids: string[]): void {
    this.appStore.getState().selection.set(ids);
  }

  public resetSelection(): void {
    this.appStore.getState().selection.set([]);
  }

  public removeSelectedIds(ids: string[]): void {
    const { selectedIds } = this.appStore.getState();
    const newSelectedIds = selectedIds.filter((selectedId) => !ids.includes(selectedId));

    this.setSelectedIds(newSelectedIds);
  }

  public setDisabledElementIds(ids: string[]): void {
    this.appStore.getState().enabling.setDisabledElementIds(ids);
  }

  public setClickableElementIds(ids: string[]): void {
    this.appStore.getState().enabling.setClickableElementIds(ids);
  }

  private deselectAll(): void {
    this.canvasSvc.deselectAll();
    this.appStore.getState().selection.clear();
  }

  //
  // Modal
  //

  public setActiveModalId(id: ModalId | null): void {
    this.appStore.getState().modal.setActiveModalId(id);
  }

  //
  // Popover
  //

  public setActivePopoverId(id: string | null): void {
    this.appStore.getState().popover.setActiveId(id);
  }

  //
  // Canvas overlay
  //

  public setCanvasOverlay(request: CanvasOverlayRequest | null): void {
    if (request === null) {
      this.appStore.getState().canvasOverlay.set(null);
      return;
    }

    this.appStore.getState().canvasOverlay.set({
      ...request,
      onCancel: request.onCancel ?? (() => this.resetTool()),
    });
  }

  //
  // Canvas viewport
  //

  public setCanvasViewport(size: SizeShort): void {
    this.appStore.getState().viewport.setViewport(size);
  }

  //
  // Revision
  //

  public async loadRevision(
    projectId: string,
    alternativeId: string,
    revisionId: string,
  ): Promise<void> {
    try {
      // A tab switch by the user while the revision loads wins over the reset to setup at the end
      const moduleAtStart = this.getActiveModule();
      this.appStore.getState().revisionLoading.setIsRevisionLoading(true);

      this.fpsSvc.reset();
      const revision = await this.projectSvc.getFloorplanRevision({
        projectId,
        alternativeId,
        revisionId,
      });

      await this.initializeRevision(revision);

      const { activeLayoutAlternative } = this.appMetaStore.getState();
      if (activeLayoutAlternative.status !== 'loaded') {
        logger.warn('Expected layoutAlternative to be loaded.');
        return;
      }

      const isLatestRevision = activeLayoutAlternative.data.lastRevisionId === revisionId;

      const loadedVehicleIds = this.floorplanDesignStore.getState().getVehicleTypeIds();
      const vehicleIdsToReconcile =
        loadedVehicleIds.length > 0 ? loadedVehicleIds : DEFAULT_ENABLED_VEHICLE_TYPE_IDS;

      await this.vehicleSvc.reconcileEnabledVehicleTypes(
        vehicleIdsToReconcile,
        projectId,
        alternativeId,
        revisionId,
      );

      if (this.canvasSvc.canvasInstanceIsInitialized()) {
        this.canvasSvc.clearFloorplanElements();
        await this.canvasSvc.updateElements({
          areas: revision.design.areas,
          flowLayer: revision.design.flows,
          templates: revision.design.templates,
          interAreaConnections: revision.design.interAreaConnections,
          jitRoutingConfigs: revision.design.justInTimeRoutingConfigs,
          logicalSpaces: revision.design.logicalSpaces,
          obstacles: revision.design.obstacles,
        });

        await this.fddmSvc.regenerateAll();
        this.referenceImageSvc.loadReferenceImage(revision.uxonly.referenceImage);

        if (this.isRevisionEmpty(revision)) {
          this.canvasSvc.zoomTo(70);
        } else {
          this.canvasSvc.zoomToFit();
        }
      }

      // Stay in 'visualization': switching to 'canvas_setup' unmounts FleetTrackerCanvas (disposing
      // its FleetTrackerClient), after which the route remounts it and the client initializes twice.
      const activeModule = this.getActiveModule();
      if (activeModule !== 'visualization' && activeModule === moduleAtStart) {
        this.goToModule('canvas_setup');
      }

      if (this.canvasSvc.canvasInstanceIsInitialized()) {
        this.canvasSvc.scaleToCurrentZoom();
      }

      this.syncFloorplanInteractionMode(true);

      this.toggleAutosaveEnabled(isLatestRevision, this.appStore.getState().activeModule);

      // IMPORTANT: void any fddm model related manipulations after this to avoid it accidentally triggering autosave
    } catch (error) {
      logger.error(error);
    } finally {
      this.appStore.getState().revisionLoading.setIsRevisionLoading(false);
    }
  }

  public disposeLoadedRevision(): void {
    this.fddmSvc.disposeState();
    this.vehicleStore.getState().reset();
    this.loadCarrierStore.getState().reset();
  }

  public async finalizeRevision(): Promise<void> {
    try {
      const { activeProject, activeLayoutAlternative } = useAppMetaStore.getState();

      if (activeProject.status !== 'loaded') {
        logger.error('Expected activeProject to be loaded');
        return;
      }

      if (activeLayoutAlternative.status !== 'loaded') {
        logger.error('Expected layoutAlternative to be loaded');
        return;
      }

      const { id: projectId } = activeProject.data;
      const { alternativeId } = activeLayoutAlternative.data;

      const finalizeRevisionResult = await this.projectSvc.finalizeLatestFloorplanRevision({
        projectId,
        alternativeId,
      });

      const { metaData, setMetaData } = useFloorplanDesignStore.getState();

      setMetaData({
        ...metaData,
        state: finalizeRevisionResult.state,
        lastUpdated: finalizeRevisionResult.lastUpdated,
        lastUpdatedBy: finalizeRevisionResult.lastUpdatedBy,
      });

      useAppMetaStore.getState().setActiveLayoutAlternative({
        ...activeLayoutAlternative.data,
        lastRevisionId: finalizeRevisionResult.revisionId,
      });

      this.syncFloorplanInteractionMode();
    } catch (error) {
      logger.error('Failed to finalize revision. error: ', error);
    } finally {
      this.resetTool();
    }
  }

  public async restoreRevision(): Promise<void> {
    const { activeProject, activeLayoutAlternative } = useAppMetaStore.getState();

    if (activeProject.status !== 'loaded') {
      logger.error('Expected activeProject to be loaded');
      return;
    }

    if (activeLayoutAlternative.status !== 'loaded') {
      logger.error('Expected layoutAlternative to be loaded');
      return;
    }

    const { id: projectId } = activeProject.data;
    const { alternativeId } = activeLayoutAlternative.data;

    const fddmStoreState = useFloorplanDesignStore.getState();
    const revisionIsInDraft = FloorplanDesignSelector.revisionIsInDraft(fddmStoreState);
    const sourceFloorplanVersionId = revisionIsInDraft ? null : fddmStoreState.metaData.revisionId;

    if (!sourceFloorplanVersionId) {
      logger.error('Could not restore version. sourceFloorplanVersionId undefined');
      return;
    }

    const newRevision = await this.projectSvc.restoreFloorplanRevision({
      projectId,
      alternativeId,
      revisionId: sourceFloorplanVersionId,
    });

    useAppMetaStore.getState().setActiveLayoutAlternative({
      ...activeLayoutAlternative.data,
      lastRevisionId: newRevision.metaData.revisionId,
    });

    await this.loadRevision(
      newRevision.metaData.projectId,
      newRevision.metaData.alternativeId,
      newRevision.metaData.revisionId,
    );

    replaceURLPath(
      `/${newRevision.metaData.projectId}/builder/${newRevision.metaData.alternativeId}/version/${newRevision.metaData.revisionId}`,
    );
  }

  public startAutosaving(): void {
    this.appStore.getState().setAutosaveEnabled(true);
  }

  public stopAutosaving(): void {
    this.appStore.getState().setAutosaveEnabled(false);
  }

  public async loadVersion(revisionId: string): Promise<void> {
    const { activeProject, activeLayoutAlternative } = useAppMetaStore.getState();

    if (activeProject.status !== 'loaded') {
      logger.error('Expected activeProject to be loaded');
      return;
    }

    if (activeLayoutAlternative.status !== 'loaded') {
      logger.error('Expected layoutAlternative to be loaded');
      return;
    }

    const { id: projectId } = activeProject.data;
    const { alternativeId } = activeLayoutAlternative.data;

    await this.loadRevision(projectId, alternativeId, revisionId);

    replaceURLPath(`/${projectId}/builder/${alternativeId}/version/${revisionId}`);
  }

  //
  // Loading container
  //

  public showLoadContainer(type: LoadingType): void {
    this.appStore.getState().loadContainer.setShowLoadContainer(true);
    this.appStore.getState().loadContainer.setLoadContainerType(type);
  }

  public hideLoadContainer(): void {
    this.appStore.getState().loadContainer.setShowLoadContainer(false);
  }

  //
  // Utility methods
  //

  private async initializeRevision(revision: FloorplanRevision): Promise<void> {
    if (revision.setup.loadTypes.length === 0) {
      DEFAULT_ENABLED_LOAD_TYPE_IDS.forEach((loadTypeId) => {
        const loadType = useLoadCarrierStore.getState().allLoadCarriers.getByIds([loadTypeId])[0];

        if (loadType) {
          revision.setup.loadTypes.push({
            id: loadType.id,
            width: loadType.width,
            height: loadType.height,
            length: loadType.length,
            weight: loadType.weight,
            category: 'PALLET',
            maxHeight: loadType.safeWorkingLoad,
            imgUrl: loadType.image.url,
          });
        }
      });
    }
    initializeNames(revision);
    this.fddmSvc.initialize(revision);
  }

  private deactivateTool(tool: ToolId): void {
    switch (tool) {
      case 'transport_flow':
      case 'consolidation_flow':
      case 'external_flow':
        this.flowSvc.cancelTool();
        break;

      case 'choose':
        this.getAreaPickerSvc().abort();
        break;

      case 'distcon':
        this.distconSvc.cancelTool();
        break;

      default:
        break;
    }
  }

  /**
   * Lazily resolve `IAreaPickerService` to avoid a constructor-time cycle
   * because the picker service depends on `AppService`.
   */
  private getAreaPickerSvc(): IAreaPickerService {
    return AreaPickerServiceFactory.getInstance().createAreaPickerService();
  }

  private loadedRevisionIsLatest(): boolean {
    const { activeLayoutAlternative } = this.appMetaStore.getState();

    if (activeLayoutAlternative.status !== 'loaded') {
      return false;
    }

    const { metaData } = this.floorplanDesignStore.getState();

    return activeLayoutAlternative.data.lastRevisionId === metaData.revisionId;
  }

  private isRevisionEmpty(revision: FloorplanRevision): boolean {
    const { design } = revision;

    return (
      hasNoAreas(design.areas) &&
      hasNoTemplates(design.templates) &&
      design.interAreaConnections.length === 0 &&
      design.justInTimeRoutingConfigs.length === 0 &&
      design.logicalSpaces.length === 0 &&
      design.obstacles.length === 0 &&
      this.isFlowLayerEmpty(design.flows)
    );
  }

  private isFlowLayerEmpty(flowLayer: FloorplanRevision['design']['flows']): boolean {
    return Object.values(flowLayer).every((value) => !Array.isArray(value) || value.length === 0);
  }

  private getFloorplanInteractionModeForModule(module: Module): FloorplanInteractionMode {
    switch (module) {
      case 'canvas_layout':
        if (this.loadedRevisionIsLatest()) {
          return 'EDITABLE';
        }

        return 'SELECT_ONLY';

      case 'canvas_setup':
      case 'canvas_viewer':
        return 'SELECT_ONLY';
      case 'canvas_simulation':
        return 'EDITABLE'; // this is required for now as the input fields will be disabled otherwise... A stupid oversight, needs a proper fix in the future.
      case 'visualization':
        return 'READONLY';

      default:
        assertNever(module);
    }
  }

  private syncFloorplanInteractionMode(force = false): void {
    const { activeModule, mode: currentMode } = this.appStore.getState();
    const mode = this.getFloorplanInteractionModeForModule(activeModule);

    if (force || currentMode !== mode) {
      this.setFloorplanInteractionMode(mode);
    }

    this.syncOriginInteractivity();
  }

  private toggleAutosaveEnabled(isLatestRevision: boolean, module: Module): void {
    switch (module) {
      case 'canvas_setup':
      case 'canvas_layout':
      case 'canvas_simulation':
      case 'canvas_viewer':
        if (isLatestRevision) {
          this.startAutosaving();
        } else {
          this.stopAutosaving();
        }
        break;
      case 'visualization':
        this.stopAutosaving();
        break;
      default:
        assertNever(module);
    }
  }

  private syncOriginInteractivity(): void {
    if (!this.canvasSvc.canvasInstanceIsInitialized()) return;

    const originInteractive =
      this.getActiveModule() === 'canvas_setup' && this.loadedRevisionIsLatest();

    this.canvasSvc.setOriginInteractive(originInteractive);
  }
}
